import { Module } from 'vuex'
import { v4 as uuid } from 'uuid'
import { ImportEntityType } from '@betterboards/shared/types/Import'
import { PlatformCompany } from '@betterboards/shared/types/Company'
import { PlatformIndividual } from '@betterboards/shared/types/Individual'
import { PlatformCommittee } from '@betterboards/shared/types/Committee'

export type PartialEntity = Partial<PlatformCompany> | Partial<PlatformIndividual> | Partial<PlatformCommittee>

export interface PendingImportItem {
  id: string
  entityType: ImportEntityType
  data: PartialEntity
  rowNumber: number
}

interface ImportState {
  pendingItems: PendingImportItem[]
}

interface AddFailedRowsPayload {
  entityType: ImportEntityType
  rows: PartialEntity[]
}

const defaultState = (): ImportState => ({
  pendingItems: []
})

const ImportStore: Module<ImportState, unknown> = {
  namespaced: true,
  state: defaultState(),
  getters: {
    pendingItems (state: ImportState): PendingImportItem[] {
      return state.pendingItems
    },
    pendingCount (state: ImportState): number {
      return state.pendingItems.length
    },
    hasPendingItems (state: ImportState): boolean {
      return state.pendingItems.length > 0
    }
  },
  mutations: {
    addPendingItem (state: ImportState, item: PendingImportItem): void {
      state.pendingItems.push(item)
    },
    addPendingItems (state: ImportState, items: PendingImportItem[]): void {
      state.pendingItems.push(...items)
    },
    removePendingItem (state: ImportState, id: string): void {
      const index = state.pendingItems.findIndex(item => item.id === id)
      if (index > -1) {
        state.pendingItems.splice(index, 1)
      }
    },
    clearPendingItems (state: ImportState): void {
      state.pendingItems.splice(0, state.pendingItems.length)
    }
  },
  actions: {
    addFailedRows ({ commit }, { entityType, rows }: AddFailedRowsPayload): void {
      const items: PendingImportItem[] = rows.map((data, index) => ({
        id: `pending-${uuid()}-${index}`,
        entityType,
        data,
        rowNumber: index + 1
      }))
      commit('addPendingItems', items)
    },
    removePendingItem ({ commit }, id: string): void {
      commit('removePendingItem', id)
    },
    clearPendingItems ({ commit }): void {
      commit('clearPendingItems')
    }
  }
}

export default ImportStore
